'use client';

/**
 * ToolTimeoutChoose — "how should we reach you?" card of the tool-timeout
 * recovery flow (lib/tool-timeout-view.ts's ToolTimeoutView.CHOOSE),
 * reachable from either ToolTimeoutOffer or ToolTimeoutFailed (and mid-retry
 * via ToolTimeoutRetrying's "Skip & leave details"). Renders identically
 * regardless of which one — the view machine tracks that distinction only
 * so BACK can return to the right card; this component doesn't need to know
 * or care which it was.
 *
 * The two option tiles are a distinct selector shape (icon + label +
 * subtitle, full-width, stacked), not the two-weight StandardCard `actions`
 * capability — that capability models a peer/quiet BUTTON pair, not a
 * radio-like tile choice, so it doesn't fit here. The "Actually, let's just
 * try again" link mirrors the quiet-Back visual language used everywhere
 * else in this tree (handoff-timeout-message.tsx et al.) but is hand-authored
 * rather than routed through `actions`, since it's a lone link below the
 * tiles, not paired with a primary button in the same row.
 */
import { ArrowLeft, Mail, Phone } from 'lucide-react';
import { cn } from '@/lib/utils';
import { V3_COLORS } from '@/lib/design-tokens';
import { StandardCard } from './primitives/standard-card';

interface ToolTimeoutChooseProps {
  onSelectMessage: () => void;
  onSelectCallback: () => void;
  onBack: () => void;
  /** Forwarded straight to the underlying StandardCard's own `className`
   *  escape hatch (see that component's header comment — it's designed for
   *  exactly this) — used by the voice-call surface's CallToolTimeoutPanel
   *  to widen this card past StandardCard's default 85%/70% transcript-lane
   *  cap so it reads comfortably inside the call surface's own notice slot.
   *  Omitted by every text-chat call site; purely additive. */
  className?: string;
}

function OptionTile({
  icon,
  label,
  subtitle,
  onClick,
}: {
  icon: React.ReactNode;
  label: string;
  subtitle: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex min-h-[44px] items-center gap-[12px] rounded-[13px] border-[1px] border-[#eadfce] bg-[#FEFCF9] px-[15px] py-[12px] text-left',
        'transition-colors hover:border-[#E8C7A0] hover:bg-[#FFF8EF]'
      )}
    >
      <span
        className="flex size-[32px] shrink-0 items-center justify-center rounded-full"
        style={{
          backgroundColor: V3_COLORS.standardCardTileBg,
          color: V3_COLORS.orange,
        }}
      >
        {icon}
      </span>
      <span className="flex flex-col gap-[1px]">
        <span
          className="text-[14px] font-semibold"
          style={{ color: V3_COLORS.ink }}
        >
          {label}
        </span>
        <span className="text-[12.5px]" style={{ color: V3_COLORS.muted }}>
          {subtitle}
        </span>
      </span>
    </button>
  );
}

export function ToolTimeoutChoose({
  onSelectMessage,
  onSelectCallback,
  onBack,
  className,
}: ToolTimeoutChooseProps) {
  return (
    <StandardCard
      eyebrow="LEAVE YOUR DETAILS"
      title="How should we reach you?"
      className={className}
    >
      <div className="flex flex-col gap-[14px]">
        <div className="flex flex-col gap-[9px]">
          <OptionTile
            icon={<Mail aria-hidden="true" className="size-[15px]" />}
            label="Send a message"
            subtitle="We reply by email"
            onClick={onSelectMessage}
          />
          <OptionTile
            icon={<Phone aria-hidden="true" className="size-[15px]" />}
            label="Get a call-back"
            subtitle="A live agent calls you"
            onClick={onSelectCallback}
          />
        </div>

        <button
          type="button"
          onClick={onBack}
          className="flex min-h-[44px] w-fit items-center gap-1 text-[13.5px] font-semibold"
          style={{ color: V3_COLORS.muted }}
        >
          <ArrowLeft aria-hidden="true" className="size-[13px]" />
          Actually, let&apos;s just try again
        </button>
      </div>
    </StandardCard>
  );
}
